import { Search } from "lucide-react";
import { useState } from "react";
import { DocTableBody } from "../../components/ui/DocTable";
import { EmptyState, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { useTree } from "../../lib/queries";
import { type DocGroup, groupByFolder } from "../tree/doc-groups";

/** The filter is there from this many documents on: a shorter list is read at a glance. */
const FILTER_FROM = 13;
const FILTER = "Filter documents by title or code";

/**
 * The Documents tab: a panel as the other tabs are, with the client's documents as the table every document list is, grouped under the client's
 * top-level pages. "New document" is beside the client's name. The order and the place of a document are changed
 * in the sidebar's tree, which is there once a document is open.
 */
export function ClientDocuments({ clientId }: { clientId: string }) {
  const { data: docs, isLoading } = useTree(clientId);
  const [text, setText] = useState("");
  const groups = groupByFolder(docs ?? []);
  const count = groups.reduce((n, g) => n + g.docs.length, 0);
  const q = count >= FILTER_FROM ? text.trim().toLowerCase() : "";
  // A filtered list is flat: a match is shown without the documents it lies below.
  const shown: DocGroup[] = q
    ? groups
        .map((g) => ({
          ...g,
          docs: g.docs
            .filter((d) => d.title.toLowerCase().includes(q) || (d.code ?? "").toLowerCase().includes(q))
            .map((d) => ({ ...d, depth: 0 })),
        }))
        .filter((g) => g.docs.length > 0)
    : groups;
  return (
    <Panel aria-label="Documents" title="Documents">
      {isLoading ? <Spinner label="Loading documents…" /> : null}
      {docs && groups.length === 0 ? (
        <EmptyState title="No documents yet" body="Start a document from a template, or a plain wiki page." />
      ) : null}
      {count >= FILTER_FROM ? (
        <label className="flex h-9 max-w-md items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 focus-within:border-green">
          <Search size={14} className="shrink-0 text-ink-3" aria-hidden="true" />
          <input
            aria-label={FILTER}
            placeholder={FILTER}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-ink-3"
          />
        </label>
      ) : null}
      {q && shown.length === 0 ? <p className="text-sm text-ink-3">No document matches “{text.trim()}”.</p> : null}
      <DocTableBody
        label="Documents"
        groups={shown}
        titleHeading="Document"
        showType
        headings="always"
        emptyNote={q ? undefined : "Nothing here yet."}
      />
    </Panel>
  );
}
